import React, { useEffect, useMemo, useState } from "react";
import { GitCompare, History } from "lucide-react";
import api, { formatErr } from "../lib/api";

/**
 * VersionsTab — pick any deliverable code and compare two versions side-by-side.
 *
 * Lists every deliverable code that has ≥2 versions, then shows v_n vs v_n-1
 * with a simple line-diff (added/removed) on the markdown content + a numerics
 * delta on `precomputed`.
 */
export default function VersionsTab({ projectId }) {
  const [allDelivs, setAllDelivs] = useState(null);
  const [openCode, setOpenCode] = useState(null);
  const [versions, setVersions] = useState(null);
  const [leftId, setLeftId] = useState(null);
  const [rightId, setRightId] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/projects/${projectId}/deliverables?all_versions=true`);
        if (!alive) return;
        setAllDelivs(data || []);
      } catch (e) {
        if (alive) setErr(formatErr(e.response?.data?.detail) || e.message);
      }
    })();
    return () => { alive = false; };
  }, [projectId]);

  const codeIndex = useMemo(() => {
    if (!allDelivs) return [];
    const map = {};
    for (const d of allDelivs) {
      if (!map[d.code]) map[d.code] = { code: d.code, name: d.name, stage_code: d.stage_code, max_version: 0 };
      if (d.version > map[d.code].max_version) {
        map[d.code].max_version = d.version;
        map[d.code].name = d.name;
        map[d.code].stage_code = d.stage_code;
      }
    }
    return Object.values(map).filter((x) => x.max_version >= 2).sort((a, b) => a.code.localeCompare(b.code));
  }, [allDelivs]);

  useEffect(() => {
    if (!openCode) { setVersions(null); return; }
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/projects/${projectId}/deliverables/by-code/${encodeURIComponent(openCode)}/versions`);
        if (!alive) return;
        setVersions(data || []);
        if (data && data.length >= 2) {
          setLeftId(data[1].id);  // v_{n-1}
          setRightId(data[0].id); // v_n
        } else if (data && data.length === 1) {
          setLeftId(null);
          setRightId(data[0].id);
        }
      } catch (e) {
        if (alive) setErr(formatErr(e.response?.data?.detail) || e.message);
      }
    })();
    return () => { alive = false; };
  }, [projectId, openCode]);

  if (err) return <div className="pf-sev pf-sev-error" style={{ display: "block", padding: 12 }}>{err}</div>;
  if (allDelivs === null) return <div style={{ padding: 24, color: "var(--pf-text-muted)" }}>Loading version history…</div>;
  if (codeIndex.length === 0) {
    return (
      <div data-testid="versions-empty" style={{ padding: 32, textAlign: "center", color: "var(--pf-text-muted)" }}>
        <History size={32} style={{ opacity: 0.4 }} />
        <div style={{ marginTop: 12, fontSize: 14 }}>No version history yet.</div>
        <div style={{ marginTop: 6, fontSize: 12 }}>Re-run any stage to bump deliverable versions; the diff viewer activates once a deliverable has ≥2 versions.</div>
      </div>
    );
  }

  const left = (versions || []).find((v) => v.id === leftId);
  const right = (versions || []).find((v) => v.id === rightId);

  return (
    <div data-testid="versions-tab" style={{ display: "grid", gridTemplateColumns: "300px 1fr", gap: 0, height: "100%" }}>
      <div style={{ borderRight: "1px solid var(--pf-border)", overflow: "auto", background: "var(--pf-surface)" }}>
        <div style={{ padding: 12, borderBottom: "1px solid var(--pf-border)" }}>
          <div className="pf-overline">DELIVERABLES WITH HISTORY · {codeIndex.length}</div>
        </div>
        <div style={{ padding: 8, display: "grid", gap: 6 }}>
          {codeIndex.map((c) => (
            <button
              key={c.code}
              type="button"
              className="pf-card"
              onClick={() => setOpenCode(c.code)}
              data-testid={`version-row-${c.code}`}
              style={{
                padding: 10, textAlign: "left", cursor: "pointer",
                borderColor: openCode === c.code ? "var(--pf-spark)" : "var(--pf-border)",
                background: openCode === c.code ? "var(--pf-surface-2)" : "var(--pf-surface)",
              }}
            >
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: "var(--pf-spark)" }}>
                {c.stage_code} · {c.code} · {c.max_version} versions
              </div>
              <div className="pf-heading" style={{ fontSize: 13, margin: "3px 0 0" }}>{c.name}</div>
            </button>
          ))}
        </div>
      </div>

      <div style={{ overflow: "auto", padding: 18 }}>
        {!openCode && (
          <div style={{ padding: 24, color: "var(--pf-text-muted)", textAlign: "center" }}>
            <GitCompare size={20} style={{ opacity: 0.4 }} />
            <div style={{ marginTop: 8, fontSize: 13 }}>Pick a deliverable on the left to compare versions.</div>
          </div>
        )}
        {openCode && versions && (
          <>
            <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 12 }}>
              <div className="pf-overline">COMPARE</div>
              <select className="pf-input" style={{ fontSize: 12, padding: "4px 8px", width: "auto" }} value={leftId || ""} onChange={(e) => setLeftId(e.target.value)} data-testid="versions-left-select">
                <option value="">— none —</option>
                {versions.map((v) => <option key={v.id} value={v.id}>v{v.version} · {v.created_at?.slice(0, 16)}</option>)}
              </select>
              <span style={{ color: "var(--pf-text-muted)" }}>vs</span>
              <select className="pf-input" style={{ fontSize: 12, padding: "4px 8px", width: "auto" }} value={rightId || ""} onChange={(e) => setRightId(e.target.value)} data-testid="versions-right-select">
                {versions.map((v) => <option key={v.id} value={v.id}>v{v.version} · {v.created_at?.slice(0, 16)}</option>)}
              </select>
            </div>
            <DiffPane left={left} right={right} />
          </>
        )}
      </div>
    </div>
  );
}

function DiffPane({ left, right }) {
  if (!right) return <div style={{ color: "var(--pf-text-muted)" }}>Pick a version.</div>;
  const ll = (left?.content_md || "").split("\n");
  const rl = (right?.content_md || "").split("\n");
  const lset = new Set(ll);
  const rset = new Set(rl);
  return (
    <div data-testid="versions-diff" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
      <div className="pf-card" style={{ padding: 12, overflow: "auto", maxHeight: "calc(100vh - 320px)" }}>
        <div className="pf-overline" style={{ marginBottom: 8 }}>{left ? `v${left.version}` : "—"} · {left?.name || ""}</div>
        <pre style={{ margin: 0, fontSize: 11.5, fontFamily: "'JetBrains Mono', monospace", whiteSpace: "pre-wrap", lineHeight: 1.5 }}>
          {ll.map((line, i) => (
            <div key={i} style={{ background: rset.has(line) ? "transparent" : "rgba(255,69,58,0.12)", padding: "0 4px" }}>
              {line || "\u00A0"}
            </div>
          ))}
        </pre>
      </div>
      <div className="pf-card" style={{ padding: 12, overflow: "auto", maxHeight: "calc(100vh - 320px)" }}>
        <div className="pf-overline" style={{ marginBottom: 8 }}>v{right.version} · {right.name}</div>
        <pre style={{ margin: 0, fontSize: 11.5, fontFamily: "'JetBrains Mono', monospace", whiteSpace: "pre-wrap", lineHeight: 1.5 }}>
          {rl.map((line, i) => (
            <div key={i} style={{ background: lset.has(line) ? "transparent" : "rgba(50,205,124,0.16)", padding: "0 4px" }}>
              {line || "\u00A0"}
            </div>
          ))}
        </pre>
      </div>
      {(left?.precomputed || right?.precomputed) && (
        <div className="pf-card" style={{ gridColumn: "1 / span 2", padding: 12 }}>
          <div className="pf-overline" style={{ marginBottom: 8 }}>NUMERIC DELTA · precomputed</div>
          <NumericDelta left={left?.precomputed || {}} right={right?.precomputed || {}} />
        </div>
      )}
    </div>
  );
}

function NumericDelta({ left, right }) {
  const flat = (o, p = "") => {
    const out = {};
    const walk = (v, k) => {
      if (v === null || v === undefined) { out[k] = ""; return; }
      if (typeof v !== "object") { out[k] = v; return; }
      if (Array.isArray(v)) { v.forEach((x, i) => walk(x, `${k}[${i}]`)); return; }
      Object.entries(v).forEach(([kk, vv]) => walk(vv, k ? `${k}.${kk}` : kk));
    };
    walk(o, p);
    return out;
  };
  const fl = flat(left);
  const fr = flat(right);
  const keys = Array.from(new Set([...Object.keys(fl), ...Object.keys(fr)])).sort();
  const changed = keys.filter((k) => String(fl[k] ?? "") !== String(fr[k] ?? ""));
  if (changed.length === 0) return <div style={{ fontSize: 12, color: "var(--pf-text-muted)" }}>No numerical change.</div>;
  return (
    <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
      <thead><tr style={{ borderBottom: "1px solid var(--pf-border)" }}>
        <th style={{ textAlign: "left", padding: 6, color: "var(--pf-text-muted)" }}>Path</th>
        <th style={{ textAlign: "left", padding: 6, color: "var(--pf-text-muted)" }}>Old</th>
        <th style={{ textAlign: "left", padding: 6, color: "var(--pf-text-muted)" }}>New</th>
      </tr></thead>
      <tbody>
        {changed.map((k) => (
          <tr key={k} style={{ borderBottom: "1px solid var(--pf-border)" }}>
            <td style={{ padding: 6, fontFamily: "'JetBrains Mono', monospace", fontSize: 11 }}>{k}</td>
            <td style={{ padding: 6, fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: "var(--pf-err)" }}>{String(fl[k] ?? "—")}</td>
            <td style={{ padding: 6, fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: "var(--pf-ok)" }}>{String(fr[k] ?? "—")}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
